import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import "./Button.css";

type Variant = "primary" | "secondary" | "ghost" | "onDark";
type Size = "md" | "lg";

interface SharedProps {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  className?: string;
}

interface ButtonAsButton
  extends SharedProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof SharedProps> {
  to?: undefined;
  href?: undefined;
}

interface ButtonAsLink
  extends SharedProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | keyof SharedProps> {
  to: string;
  href?: undefined;
}

interface ButtonAsAnchor
  extends SharedProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof SharedProps> {
  to?: undefined;
  href: string;
}

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

/**
 * Renders as an internal <Link>, an external <a>, or a <button>
 * depending on which of `to` / `href` is supplied.
 */
export default function Button({
  variant = "primary",
  size = "md",
  children,
  className = "",
  ...rest
}: ButtonProps) {
  const classes = `btn btn--${variant} btn--${size} ${className}`.trim();

  if ("to" in rest && rest.to) {
    const { to, ...anchorRest } = rest;
    return (
      <Link to={to} className={classes} {...anchorRest}>
        {children}
      </Link>
    );
  }

  if ("href" in rest && rest.href) {
    const { href, ...anchorRest } = rest;
    return (
      <a href={href} className={classes} {...anchorRest}>
        {children}
      </a>
    );
  }

  const { ...buttonRest } = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button className={classes} {...buttonRest}>
      {children}
    </button>
  );
}
